import { useState, useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { IconChevronRight } from '@/components/icons/Icons'

/*
  Slot de cabecera: si el layout expone `#portal-crumbs-slot` (el portal del
  cliente lo pone en su barra superior), la ruta se dibuja ahí en vez de sobre
  el contenido. En el admin no existe ese nodo, así que se renderiza inline y
  nada cambia.
*/
const SLOT_ID = 'portal-crumbs-slot'

/*
  Breadcrumbs — ruta de la página. Reemplaza al `<h1>` como identificador de
  pantalla: no usamos títulos grandes, el usuario se ubica por la ruta.
  El último item es siempre el actual (sin enlace, aunque traiga `to`).

  Envuelve `.breadcrumbs` de index.css.
*/
export interface Crumb {
  label: ReactNode
  to?: string
  /**
   * Vuelta a una vista que no es una ruta propia (un detalle que se abre
   * dentro de su listado). Se ignora si el crumb es el último o si trae `to`.
   */
  onClick?: () => void
}

interface BreadcrumbsProps {
  items: Crumb[]
  /** Contador o dato auxiliar a la derecha de la ruta (ej. "23 pagos"). */
  meta?: ReactNode
}

export function Breadcrumbs({ items, meta }: BreadcrumbsProps) {
  // El slot se busca tras montar: cuando la página se dibuja, el layout ya existe.
  const [slot, setSlot] = useState<HTMLElement | null>(null)
  useEffect(() => { setSlot(document.getElementById(SLOT_ID)) }, [])

  const nav = (
    <nav className="breadcrumbs" aria-label="Ruta de navegación">
      {items.map((c, i) => {
        const last = i === items.length - 1
        return (
          <span className="breadcrumbs__item" key={i}>
            {i > 0 && <IconChevronRight size={12} className="breadcrumbs__sep" />}
            {c.to && !last
              ? <Link to={c.to} className="breadcrumbs__link">{c.label}</Link>
              : c.onClick && !last
                ? <button type="button" className="breadcrumbs__link" onClick={c.onClick}>{c.label}</button>
                : <span className="breadcrumbs__current" aria-current={last ? 'page' : undefined}>{c.label}</span>}
          </span>
        )
      })}
      {meta != null && <span className="breadcrumbs__meta">{meta}</span>}
    </nav>
  )

  return slot ? createPortal(nav, slot) : nav
}
